/**
 * SessionTimer — 좌측 사이드바 하단 세션 잔여시간 카운트다운.
 *
 * admin_at 쿠키는 httpOnly 라 JS 가 못 읽으므로 /auth/session 으로 만료시각(exp)을 받아
 * 절대시각 기준으로 매초 잔여를 계산(드리프트 없음). 만료(0) 도달 시 clearSession → 로그인 화면.
 * 사이드바 접힘 시에는 라벨이 숨고 아이콘+시간만 노출(admin-console__label 규칙 공유).
 */
import { useEffect, useRef, useState } from 'react';
import { Clock } from '../../icons';
import { fetchAdminSession, refreshAdminSession } from '../../api/auth';
import { useAdminAuthStore } from '../../store/adminAuthStore';

// 활동 감지 시 세션 재발급 throttle — 활동해도 60초에 1회만 refresh(요청 폭주 방지).
const RENEW_THROTTLE_MS = 60_000;

function fmt(sec: number): string {
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

export function SessionTimer() {
  const clearSession = useAdminAuthStore((s) => s.clearSession);
  const [exp, setExp] = useState<number | null>(null);
  const [ttl, setTtl] = useState<number>(3600);
  const [remaining, setRemaining] = useState<number | null>(null);
  const expiredRef = useRef(false);
  const lastRenewRef = useRef(0);
  const expRef = useRef<number | null>(null);
  expRef.current = exp;

  // 세션 정보 로드 + 주기적 재동기화(세션 갱신/드리프트 대비).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const s = await fetchAdminSession();
        if (cancelled) return;
        setExp(s.exp);
        setTtl(s.ttl_sec || 3600);
        expiredRef.current = false;
      } catch {
        /* 401 → apiClient 인터셉터가 clearSession 처리 */
      }
    };
    void load();
    const resync = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(resync); };
  }, []);

  // 매초 잔여 계산(절대 exp 기준).
  useEffect(() => {
    if (exp == null) return;
    const tick = () => {
      const rem = exp - Math.floor(Date.now() / 1000);
      setRemaining(rem);
      if (rem <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        clearSession();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [exp, clearSession]);

  // 활동 시 자동 연장(sliding renewal) — 마우스/키보드/터치 감지, 60초 throttle.
  useEffect(() => {
    const onActivity = () => {
      const now = Date.now();
      if (now - lastRenewRef.current < RENEW_THROTTLE_MS) return;
      const e = expRef.current;
      // 이미 만료됐거나 아직 세션 미로드면 연장 안 함(만료는 재로그인 필요).
      if (e == null || e - Math.floor(now / 1000) <= 0) return;
      lastRenewRef.current = now;
      refreshAdminSession()
        .then((s) => { setExp(s.exp); expiredRef.current = false; })
        .catch(() => { /* 401 → 인터셉터가 처리 */ });
    };
    const opts = { passive: true } as const;
    window.addEventListener('mousedown', onActivity, opts);
    window.addEventListener('keydown', onActivity, opts);
    window.addEventListener('touchstart', onActivity, opts);
    return () => {
      window.removeEventListener('mousedown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('touchstart', onActivity);
    };
  }, []);

  if (remaining == null) return null;

  const rem = Math.max(0, remaining);
  const pct = Math.max(0, Math.min(100, (rem / ttl) * 100));
  const state = rem <= 0 ? 'expired' : rem <= 120 ? 'danger' : rem <= 600 ? 'warn' : 'ok';

  return (
    <div className={`admin-console__session admin-console__session--${state}`} title={`세션 잔여 ${fmt(rem)}`}>
      <div className="admin-console__session-row">
        <span className="admin-console__session-icon" aria-hidden>
          <Clock size={15} />
        </span>
        <span className="admin-console__session-label admin-console__label">세션 유지</span>
        <span className="admin-console__session-time" role="timer" aria-live="off">
          {state === 'expired' ? '만료' : fmt(rem)}
        </span>
      </div>
      <div className="admin-console__session-bar" aria-hidden>
        <span className="admin-console__session-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
